import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import NextImage from 'next/image';
import { Styles } from '@t2c/shared-fe';
import { FilteringSortingParams } from '../utils';
import { useFilteringAndSorting } from '../hooks/useFilteringAndSorting';
import { Iconify } from '@sdf/base';

interface FilterSortingArray {
  id: number;
  name: string;
  onClick?: () => any;
  value: string;
}

interface FiltersAndSortingProps {
  params: FilteringSortingParams;
  setParams: (_: FilteringSortingParams) => void;
  filterArray?: Array<FilterSortingArray>;
  handleFilterOptionChange?: (_) => void;
  showFilters?: boolean;
  showSorting?: boolean;
}

export const FiltersAndSorting: React.FC<FiltersAndSortingProps> = ({
  params,
  setParams,
  filterArray = [],
  handleFilterOptionChange,
  showFilters = true,
  showSorting = true,
}) => {
  const {
    anchorEl,
    isFiltersOpen,
    isSortingOpen,
    handleFilterMenuClick,
    handleSortMenuClick,
    sortingArray,
    handleSortOrderChange,
    setAnchorEl,
    setIsSortingOpen,
    setIsFiltersOpen,
    handleResetFilter,
  } = useFilteringAndSorting({ params, setParams });
  const theme = useTheme();

  return (
    <Box sx={styles.container}>
      <Box sx={styles.filtersAndSorting}>
        {showFilters && (
          <Box sx={styles.filterBox} onClick={handleFilterMenuClick}>
            <Typography>Filters</Typography>
            <Iconify icon="mdi:filter" width="24px" height="24px" />
            {/* <NextImage src={'/assets/icons/FilterIcon.svg'} alt="" height={24} width={24} /> */}
          </Box>
        )}
        {showSorting && (
          <Box sx={styles.filterBox} onClick={handleSortMenuClick}>
            <Typography>Sort By</Typography>
            <Iconify icon="flowbite:sort-outline" width="24px" height="24px" />
            {/* <NextImage src={'/assets/icons/Sorting.svg'} alt="" height={24} width={24} /> */}
          </Box>
        )}
      </Box>

      {showSorting && (
        <Menu
          anchorEl={anchorEl}
          open={isSortingOpen}
          MenuListProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.background.secondary,
            },
          }}
          slotProps={{
            paper: {
              style: {
                background: 'transparent',
                border: `1px solid ${theme.palette.background.border}`,
              },
            },
          }}
          onClose={() => {
            setAnchorEl(null);
            setIsSortingOpen(false);
          }}
          sx={{ mt: 2 }}
        >
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={params.sortOrder}
            onChange={(e) => handleSortOrderChange(e.target.value)}
            sx={{ paddingX: 2 }}
          >
            <FormControlLabel
              value="asc"
              control={<Radio size="small" />}
              label={<Typography sx={styles.menuText}>Ascending</Typography>}
            />
            <FormControlLabel
              value="desc"
              control={<Radio size="small" />}
              label={<Typography sx={styles.menuText}>Descending</Typography>}
            />
          </RadioGroup>
          <Divider sx={{ mb: 1 }} />
          {sortingArray.map((res) => (
            <MenuItem
              sx={{
                ...styles.menuText,
                bgcolor: (theme) =>
                  params.sortBy === res.value ? theme.palette.background.activeCard : '',
              }}
              onClick={res.onClick}
              key={res.id}
            >
              {res.name}
            </MenuItem>
          ))}
        </Menu>
      )}

      {showFilters && (
        <Menu
          anchorEl={anchorEl}
          open={isFiltersOpen}
          onClose={() => {
            setIsFiltersOpen(false);
            setAnchorEl(null);
          }}
          sx={{ mt: 2 }}
          MenuListProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.background.secondary,
            },
          }}
          slotProps={{
            paper: {
              style: {
                background: 'transparent',
                border: `1px solid ${theme.palette.background.border}`,
              },
            },
          }}
        >
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={params?.filters?.renderStatus ?? params?.filters?.status}
            onChange={(e) => handleFilterOptionChange?.(e.target.value)}
          >
            {filterArray.map((res, index) => (
              <MenuItem key={index}>
                <FormControlLabel
                  value={res.value}
                  control={<Radio size="small" />}
                  label={<Typography sx={styles.menuText}>{res.name}</Typography>}
                />
              </MenuItem>
            ))}
            <Divider />
            <MenuItem sx={styles.menuText} onClick={handleResetFilter}>
              Reset
            </MenuItem>
          </RadioGroup>
        </Menu>
      )}
    </Box>
  );
};

const styles: Styles = {
  filterBox: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: (theme) => theme.palette.background.primary,
    padding: 1,
    borderRadius: '6px',
    border: (theme) => `1px solid ${theme.palette.background.border}`,
    width: '123px',
    cursor: 'pointer',
    '&:hover': {
      border: (theme) => `1px solid ${theme.palette.background.activeBorder}`,
    },
  },
  menuText: {
    color: (theme) => theme.palette.text.primary,
    fontSize: '12px',
  },
  filtersAndSorting: {
    display: 'flex',
    gap: 2,
  },
};
